import {useContext, useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {
  useFetchDefaultRegistryProjectsQuery,
  useUpsertRegistryProjectMutation,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {
  doNotRetryContext,
  propagateErrorsContext,
} from '../../../../util/errors';
import {AccessOption} from '../../../../util/permissions';
import {useIsOrgReadOnly} from '../../common/hooks';
import {RegistryAccessOptionType} from '../../common/permissionUtils';
import {RegistriesSearchContext} from '../../common/RegistriesSearchContextProvider';
import {
  checkRegistryProject,
  DEFAULT_REGISTRY_PROJECT_TYPES,
  globalRegistryType,
  REGISTRY_PROJECT_PREFIX,
} from '../../common/utils';
import {FETCH_REGISTRY_PROJECTS_PARTIAL} from '../../graphql/registryProjects.query';
import {FETCH_REGISTRIES_PAGE_SIZE} from '../../RegistriesHomepage/hooks';
import {getRegistryProjectsFromQuery} from './utils';

export type UpsertAction = 'Create' | 'Edit';

type UpsertRegistryProjectInputType = {
  upsertAction: UpsertAction;
  description: string;
  registryProjectName: string;
  entityName: string;
  access: RegistryAccessOptionType;
  inputArtifactTypes: string[];
  allowAllArtifactTypesInRegistry?: boolean;
  ownerId?: string;
};

export function useUpsertRegistryProjectWithTypes(
  orgName: string,
  showToast = true
) {
  const {searchPattern} = useContext(RegistriesSearchContext);
  const [upsertRegistryProjectMutation] = useUpsertRegistryProjectMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName,
          pattern: searchPattern,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
      },
      {
        query: FETCH_REGISTRY_PROJECTS_PARTIAL,
        variables: {
          orgName,
          limit: FETCH_REGISTRIES_PAGE_SIZE,
        },
      },
    ],
  });
  const [isLoading, setIsLoading] = useState(false);
  async function upsertRegistryProjectWithTypes(
    input: UpsertRegistryProjectInputType
  ) {
    const {
      upsertAction,
      description,
      registryProjectName,
      entityName,
      access,
      inputArtifactTypes,
      allowAllArtifactTypesInRegistry,
      ownerId,
    } = input;
    const isEditMode = upsertAction === 'Edit';
    let upsertRegistryProject;
    setIsLoading(true);
    try {
      if (
        entityName !== '' &&
        registryProjectName !== '' &&
        allowAllArtifactTypesInRegistry != null
      ) {
        let artifactTypes = undefined;
        if (inputArtifactTypes.length > 0) {
          artifactTypes = inputArtifactTypes.map((artifactType: string) => ({
            name: artifactType,
          }));
        }
        const trimmedRegistryProjectName = registryProjectName.trim();
        upsertRegistryProject = await upsertRegistryProjectMutation({
          variables: {
            description,
            name: trimmedRegistryProjectName,
            entityName,
            access,
            allowAllArtifactTypesInRegistry,
            owner: ownerId,
            artifactTypes,
          },
        });
        const {registryName} = checkRegistryProject(trimmedRegistryProjectName);
        if (
          registryName != null &&
          DEFAULT_REGISTRY_PROJECT_TYPES.includes(registryName)
        ) {
          Analytics.track('Registry interaction', {
            action: 'create custom registry',
            entityName,
            registryProjectName,
            registryName,
          });
        }
      }
      if (showToast) {
        toast(isEditMode ? 'Updated registry settings!' : 'Created registry!');
      }
      setIsLoading(false);
      return upsertRegistryProject;
    } catch (error) {
      toast(
        `Something went wrong while trying to ${
          isEditMode ? 'update' : 'create'
        } the registry`
      );
      setIsLoading(false);
      return undefined;
    }
  }
  return {upsertRegistryProjectWithTypes, isLoading};
}

export function useSetupDefaultRegistries(orgName: string) {
  const {isOrganizationReadOnly: readOnly} = useIsOrgReadOnly();
  const [registries, setRegistries] = useState<Map<
    string,
    globalRegistryType
  > | null>(null);
  const [mutationQueue, setMutationQueue] = useState<
    UpsertRegistryProjectInputType[]
  >([]);
  const [executionQueue, setExecutionQueue] = useState<string[]>([]);

  const defaultRegistryNames = useMemo(
    () =>
      DEFAULT_REGISTRY_PROJECT_TYPES.map(
        type => `${REGISTRY_PROJECT_PREFIX}${type}`
      ),
    []
  );

  const defaultRegistryFilters = useMemo(
    () => ({
      name: {
        $in: defaultRegistryNames,
      },
    }),
    [defaultRegistryNames]
  );
  const {
    data,
    loading: defaultRegistryProjectsQueryLoading,
    refetch,
    variables,
  } = useFetchDefaultRegistryProjectsQuery({
    variables: {
      orgName,
      filters: JSON.stringify(defaultRegistryFilters),
    },
    context: propagateErrorsContext(),
  });

  const {upsertRegistryProjectWithTypes, isLoading: upsertLoading} =
    useUpsertRegistryProjectWithTypes(orgName, false);

  useEffect(() => {
    if (!defaultRegistryProjectsQueryLoading && !upsertLoading) {
      const {registries} = getRegistryProjectsFromQuery(orgName, data);
      setRegistries(registries);
      const existingDefaultRegistryNames: string[] = [];
      if (registries != null && !readOnly) {
        for (const registryName of registries.keys()) {
          if (DEFAULT_REGISTRY_PROJECT_TYPES.includes(registryName)) {
            existingDefaultRegistryNames.push(registryName);
          }
        }

        const newMutations = DEFAULT_REGISTRY_PROJECT_TYPES.reduce(
          (acc: UpsertRegistryProjectInputType[], regName) => {
            if (
              !existingDefaultRegistryNames.includes(regName) &&
              !executionQueue.includes(regName)
            ) {
              acc.push({
                upsertAction: 'Create',
                description: `Store & share ${regName} artifacts`,
                registryProjectName: `${REGISTRY_PROJECT_PREFIX}${regName}`,
                entityName: data?.organization?.orgEntity?.name ?? '',
                access: AccessOption.Private,
                inputArtifactTypes: [regName],
                allowAllArtifactTypesInRegistry: false,
              });
            }
            return acc;
          },
          []
        );
        setMutationQueue(prev => [...prev, ...newMutations]);
      }
    }
  }, [
    data,
    upsertLoading,
    defaultRegistryProjectsQueryLoading,
    executionQueue,
    readOnly,
    variables,
    orgName,
  ]);

  // Execute queued mutations
  useEffect(() => {
    (async () => {
      if (
        mutationQueue.length > 0 &&
        !upsertLoading &&
        !defaultRegistryProjectsQueryLoading
      ) {
        const currentMutation = mutationQueue[0];
        const {registryName: regName} = checkRegistryProject(
          currentMutation.registryProjectName
        );
        if (executionQueue.includes(regName!)) {
          setMutationQueue(prev => prev.slice(1));
        } else {
          try {
            setExecutionQueue(prev => [...prev, regName!]);
            await upsertRegistryProjectWithTypes(currentMutation);
            setMutationQueue(prev => prev.slice(1));
          } catch (error) {
            setMutationQueue([]);
          }
        }
      }
    })();
  }, [
    defaultRegistryProjectsQueryLoading,
    upsertLoading,
    executionQueue,
    mutationQueue,
    mutationQueue.length,
    upsertRegistryProjectWithTypes,
  ]);

  useEffect(() => {
    refetch({orgName, filters: JSON.stringify(defaultRegistryFilters)});
  }, [refetch, orgName, defaultRegistryFilters]);

  return {
    defaultRegistries: registries ?? new Map(),
    loading: upsertLoading || defaultRegistryProjectsQueryLoading,
    orgId: data?.organization?.id ?? '',
    entityName: data?.organization?.orgEntity?.name ?? '',
  };
}
